
// client/src/components/Dashboard.jsx
// Modern Dashboard with KPI cards, calendar, embedded lists, and task list.
// Backward-compatible: retains all original data fetching, event/task logic,
// push subscription, modal drill-down, and footer actions.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { urlBase64ToUint8Array } from '../utils/pushUtils';
import { format, parseISO, isSameDay, addDays, startOfToday } from 'date-fns';
import AppFooter from './AppFooter';
import { FaList, FaPlus, FaPaperPlane, FaBell, FaChartBar } from 'react-icons/fa';
import ScheduledPostsList from './media/ScheduledPostsList';
import ScheduledEventsList from './event/ScheduledEventsList';
import DashboardTaskList from './DashboardTaskList';
import PendingInvitations from './tenant/PendingInvitations';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';

const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function Dashboard() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalFilter, setModalFilter] = useState('all'); // 'all' | 'events' | 'tasks'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { tenant } = useParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  // Resolve username reliably (works with your current storage pattern)
  function getUsernameSafe() {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null') || {};
      const me = JSON.parse(localStorage.getItem('me') || 'null') || {};
      const s  = JSON.parse(localStorage.getItem('session') || 'null') || {};
      return (
        u.username ||
        s.username ||
        me.username ||
        (window.__ME__ && window.__ME__.username) ||
        localStorage.getItem('username') ||
        null
      );
    } catch { return localStorage.getItem('username') || null; }
  }
  const username = getUsernameSafe();

  const today = React.useMemo(() => startOfToday(), []);
  const currentDay = today;
  const remainingDays = React.useMemo(
    () => Array.from({ length: 6 }, (_, i) => addDays(today, i + 1)),
    [today]
  );

  // PUSH SUBSCRIPTION EFFECT
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready
      .then(swReg => {
        return swReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
        });
      })
      .then(subscription => {
        return axios.post(`${baseUrl}/api/notifications/push/subscribe`, { subscription });
      })
      .catch(err => console.error('Push subscribe failed:', err));
  }, [baseUrl]);

  // OPTIMIZED: Combined data fetching in a single effect
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const todayStr = format(today, 'yyyy-MM-dd');

        const [eventRes, taskRes] = await Promise.all([
          axios.get(`${baseUrl}/api/events/${tenant}/events`),
          axios.get(`${baseUrl}/api/events/${tenant}/tasks`, {
            params: { date: todayStr, username }
          })
        ]);

        if (eventRes.data.success) setEvents(eventRes.data.data || []);
        if (taskRes.data.success) setTasks(taskRes.data.tasks || []);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (!tenant) return;
    if (!username) {
      setError('Failed to load dashboard data');
      setLoading(false);
      return;
    }
    fetchAll();
  }, [tenant, baseUrl, username]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDayClick = (day, filter = 'all') => {
    setSelectedDay(day);
    setModalFilter(filter);
    setShowModal(true);
  };

  // Active = not completed and not cancelled.
  // Matches the "active" semantics already used by DashboardTaskList's backend filter.
  const isActiveTask = (task) => {
    const s = (task.status || '').toLowerCase();
    return s !== 'completed' && s !== 'cancelled';
  };

  const closeModal = () => setShowModal(false);

  // Check if user is mentioned in a task
  const isUserMentioned = (task) => {
    const me = (username || '').toLowerCase();
    const created = (task.createdby || task.createdBy || '').toLowerCase();
    const comment = (task.comment || '').toLowerCase();
    return created === me || comment.includes(`@${me}`);
  };

  const isTimelineEvent = (event) => {
    const name = String(event?.name ?? event?.title ?? '').trim().toLowerCase();
    const type = String(event?.type ?? event?.eventtype ?? '').trim().toLowerCase();
    return name === 'timeline' || type === 'timeline';
  };

  const getEventsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return events.filter((event) => {
      if (isTimelineEvent(event)) return false;
      if (!event?.startdate || !event?.enddate) return false;
      const start = parseISO(event.startdate);
      const end = parseISO(event.enddate);
      if (isNaN(start) || isNaN(end)) return false;
      // Compare calendar dates only so events appear on the day they're scheduled,
      // independent of the time-of-day boundary.
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(end, 'yyyy-MM-dd');
      return dayStr >= startStr && dayStr <= endStr;
    });
  };

  const getTasksForDay = (day) => {
    return tasks.filter(task =>
      task.taskdate && isSameDay(new Date(task.taskdate), day) && isUserMentioned(task)
    );
  };

  const handleNewPost = () => {
    navigate(`/${tenant}/post/new`);
  };

  // ── KPI calculations ──
  const todayEvents = getEventsForDay(currentDay);
  const todayTasks = getTasksForDay(currentDay);
  const todayActiveTasks = todayTasks.filter(isActiveTask);
  const urgentTasks = tasks.filter(
    t => (t.priority || '').toLowerCase() === 'urgent' || (t.priority || '').toLowerCase() === 'high'
  );

  // ── Upcoming days rows for grid ──
  const upcomingDays = remainingDays;

  if (loading) return <p>{t('dashboard.loadingText')}</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="dashboard-modern" style={{ paddingBottom: 80 }}>

      {/* ── Header ── */}
      <div className="dashboard-header">
        <h2>{t('dashboard.title')}</h2>
      </div>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${tenant}/events`)}>
          <FaList /> {t('dashboard.eventListButton')}
        </button>
        <button type="button" onClick={handleNewPost} disabled={loading}>
          <FaPaperPlane /> {t('dashboard.postButton')}
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/analytics`)}>
          <FaChartBar /> {t('dashboard.analyticsButton', 'Analytics')}
        </button>
        <button type="button" className="btn-primary-action" onClick={() => navigate(`/${tenant}/eventAdd`)}>
          <FaPlus /> {t('dashboard.addEventButton')}
        </button>
      </div>

      {/* ── Pending Invitations (if any) ── */}
      <PendingInvitations />

      {/* ── KPI Cards ── */}
      <div className="dashboard-kpi-row">
        <div className="dashboard-kpi-card" onClick={() => handleDayClick(currentDay, 'events')} style={{ cursor: 'pointer' }}>
          <div className="dashboard-kpi-value">{todayEvents.length}</div>
          <div className="dashboard-kpi-label">{t('dashboard.kpiEvents', 'Events Today')}</div>
        </div>
        <div className="dashboard-kpi-card" onClick={() => handleDayClick(currentDay, 'tasks')} style={{ cursor: 'pointer' }}>
          <div className="dashboard-kpi-value">{todayActiveTasks.length}</div>
          <div className="dashboard-kpi-label">{t('dashboard.kpiTasks', 'Tasks Today')}</div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-value" style={{ color: urgentTasks.length > 0 ? '#dc3545' : undefined }}>
            {urgentTasks.length}
          </div>
          <div className="dashboard-kpi-label">{t('dashboard.kpiUrgent', 'Urgent/High')}</div>
        </div>
      </div>

      {/* ── Alerts bar (only if urgent tasks exist) ── */}
      {urgentTasks.length > 0 && (
        <div className="dashboard-alerts-bar">
          <FaBell className="alert-icon" />
          <span>
            {t('dashboard.urgentAlert', {
              count: urgentTasks.length,
              defaultValue: `You have ${urgentTasks.length} urgent/high priority task(s) requiring attention.`,
            })}
          </span>
        </div>
      )}

      {/* ── Today Card ── */}
      <div className="dashboard-today-card" onClick={() => handleDayClick(currentDay)}>
        <div className="dashboard-today-header">
          <span className="today-day">{t('dashboard.todayLabel', 'Today')}</span>
          <span className="today-date">{format(currentDay, 'EEEE MMMM do, yyyy')}</span>
        </div>
        <div className="dashboard-today-items">
          {[...todayEvents, ...todayTasks].length === 0 && (
            <div className="dashboard-today-empty">
              {t('dashboard.noEventsTasksMessage')}
            </div>
          )}
          {[...todayEvents.map(e => ({ ...e, _type: 'event' })),
            ...todayTasks.map(tk => ({ ...tk, _type: 'task' }))]
            .slice(0, 5)
            .map((item, i) => (
              <div key={i} className="dashboard-today-item">
                <span className={`item-dot ${item._type}`} />
                <span>{item.title || item.name}</span>
              </div>
            ))}
          {(todayEvents.length + todayTasks.length) > 5 && (
            <div style={{ textAlign: 'center', padding: '4px 0' }}>
              <small style={{ color: '#ff6800' }}>
                +{todayEvents.length + todayTasks.length - 5} {t('common.more', 'more')}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* ── Scheduled Posts (embedded) ── */}
      <div className="dashboard-embedded-list">
        <ScheduledPostsList
          tenantId={Number(tenant)}
          embedded={true}
        />
      </div>

      {/* ── Scheduled Events (embedded) ── */}
      <div className="dashboard-embedded-list">
        <ScheduledEventsList
          tenantId={Number(tenant)}
          embedded={true}
        />
      </div>

      {/* ── Task List (embedded, role-aware) ── */}
      <div className="dashboard-embedded-list">
        <DashboardTaskList
          tenantId={Number(tenant)}
          embedded={true}
        />
      </div>

      {/* ── Upcoming Days Grid ── */}
      <div className="dashboard-days-grid">
        {upcomingDays.map((day, index) => {
          const eventsToday = getEventsForDay(day);
          const tasksToday = getTasksForDay(day);
          const dotCount = Math.min(8, eventsToday.length + tasksToday.length);

          return (
            <div
              key={index}
              className="dashboard-day-card"
              onClick={() => handleDayClick(day)}
            >
              <div className="day-name">{format(day, 'EEEE')}</div>
              <div className="day-date">{format(day, 'MMM do')}</div>
              <div className="day-dots">
                {[...Array(dotCount)].map((_, i) => (
                  <span
                    key={i}
                    className={`dot ${i < eventsToday.length ? 'event' : 'task'}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal with event + task list ── */}
      {showModal && (() => {
        const showEvents = modalFilter !== 'tasks';
        const showTasks = modalFilter !== 'events';
        const modalEvents = showEvents ? getEventsForDay(selectedDay) : [];
        // Tasks-only view scopes to active tasks (matches the Tasks Today KPI count).
        let modalTasks = [];
        if (showTasks) {
          const dayTasks = getTasksForDay(selectedDay);
          modalTasks = modalFilter === 'tasks' ? dayTasks.filter(isActiveTask) : dayTasks;
        }
        const dayLabel = format(selectedDay, 'EEEE');
        const dateLabel = format(selectedDay, 'MMMM d, yyyy');
        const isEmpty = modalEvents.length === 0 && modalTasks.length === 0;

        return (
          <div className="dashboard-modal-overlay" onClick={closeModal}>
            <div className="dashboard-modal-content" onClick={(e) => e.stopPropagation()}>

              {/* Header with day name, full date, and item counts */}
              <div className="dashboard-modal-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{dayLabel}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: 2 }}>{dateLabel}</div>
                  </div>
                  <button
                    onClick={closeModal}
                    aria-label="Close"
                    style={{
                      background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                      width: 32, height: 32, borderRadius: '50%', fontSize: '1.1rem',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    &times;
                  </button>
                </div>
                {/* Summary counts */}
                {!isEmpty && (
                  <div style={{
                    display: 'flex', gap: 16, marginTop: 8,
                    fontSize: '0.8rem', opacity: 0.9,
                  }}>
                    <span>{modalEvents.length} {modalEvents.length === 1 ? 'Event' : 'Events'}</span>
                    <span>{modalTasks.length} {modalTasks.length === 1 ? 'Task' : 'Tasks'}</span>
                  </div>
                )}
              </div>

              <div className="dashboard-modal-body">

                {/* ── Events Section ── */}
                {modalEvents.length > 0 && (
                  <div className="modal-detail-section">
                    <div className="modal-detail-section-label">
                      <span className="item-dot event" style={{
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: '#ff6800', display: 'inline-block', marginRight: 6,
                      }} />
                      Events
                    </div>
                    {modalEvents.map((event) => (
                      <div
                        key={`event-${event.id}`}
                        className="modal-detail-card"
                        onClick={() => navigate(`/${tenant}/${event.id}/eventDetail`)}
                      >
                        <div className="modal-detail-card-left">
                          <div className="modal-detail-card-title">{event.name}</div>
                          {event.startdate && event.enddate && (
                            <div className="modal-detail-card-meta">
                              {format(parseISO(event.startdate), 'MMM d')} &ndash; {format(parseISO(event.enddate), 'MMM d')}
                            </div>
                          )}
                        </div>
                        <span className="modal-detail-card-arrow">&rsaquo;</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Tasks Section ── */}
                {modalTasks.length > 0 && (
                  <div className="modal-detail-section">
                    <div className="modal-detail-section-label">
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: '#253866', display: 'inline-block', marginRight: 6,
                      }} />
                      Tasks
                    </div>
                    {modalTasks.map((task) => (
                      <div
                        key={`task-${task.id}`}
                        className="modal-detail-card"
                        onClick={() => {
                          const detailPath = task.eventitemid
                            ? `/${tenant}/${task.eventid}/${task.eventitemid}/eventItemDetail`
                            : `/${tenant}/${task.eventid}/eventDetail`;
                          navigate(detailPath);
                        }}
                      >
                        <div className="modal-detail-card-left">
                          <div className="modal-detail-card-title">{task.title}</div>
                          {task.comment && (
                            <div className="modal-detail-card-meta">{task.comment}</div>
                          )}
                          {task.status && (
                            <span className={`badge badge-sm ${
                              task.status === 'completed' ? 'bg-success' :
                              task.status === 'in-progress' ? 'bg-info' :
                              task.status === 'blocked' ? 'bg-danger' : 'bg-secondary'
                            }`} style={{ fontSize: '0.65rem', marginTop: 4 }}>
                              {task.status.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="modal-detail-card-arrow">&rsaquo;</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Empty state ── */}
                {isEmpty && (
                  <div className="dashboard-modal-empty">
                    <div style={{ fontSize: '2rem', marginBottom: 8, opacity: 0.3 }}>&#128197;</div>
                    {t('dashboard.noEventsTasksMessage')}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="dashboard-modal-footer">
                <button onClick={closeModal} className="btn btn-secondary btn-sm">
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bottom action bar ── */}
      <AppFooter
        buttons={[
          {
            icon: <FaList />,
            label: t('dashboard.eventListButton'),
            onClick: () => navigate(`/${tenant}/events`),
          },
          {
            id: "new",
            icon: <FaPaperPlane />,
            label: t('dashboard.postButton'),
            onClick: handleNewPost,
            disabled: loading,
          },
          {
            icon: <FaChartBar />,
            label: t('dashboard.analyticsButton', 'Analytics'),
            onClick: () => navigate(`/${tenant}/analytics`),
          },
          {
            icon: <FaPlus />,
            label: t('dashboard.addEventButton'),
            onClick: () => navigate(`/${tenant}/eventAdd`),
          },
        ]}
      />
    </div>
  );
}

export default Dashboard;
